/* eslint-disable react-hooks/set-state-in-effect, @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSession, useAuth } from "../../context/AuthContext";
import { usePlans } from "../../context/PlanContext";
import api from "../../services/api";

export default function Sidebar() {
  const location = useLocation();
  const pathname = location.pathname;
  const { data: session } = useSession();
  const { logout } = useAuth();
  const { getPlanByName } = usePlans();
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  const role = (session?.user as any)?.role || "STUDENT";
  const membership = (session?.user as any)?.highest_plan_name || (session?.user as any)?.membership || "FREE";
  
  let userHighestTier = (session?.user as any)?.highest_tier_level;
  if (userHighestTier === undefined) {
    const userPlan = getPlanByName(membership);
    userHighestTier = userPlan ? userPlan.tier_level : 0;
  }

  const userId = (session?.user as any)?.id;
  const userName = session?.user?.name || "The Curator";

  const fetchUnread = useCallback(async () => {
    if (!userId || role === "STUDENT") return;
    try {
      const res = await api.get(`/discussions/unread-count/${userId}?t=${Date.now()}`);
      setUnreadCount(res.data.count);
    } catch (err) {
      console.error("Failed to fetch unread count:", err);
    }
  }, [userId, role]);

  useEffect(() => {
    fetchUnread();
    
    window.addEventListener("discussionRead", fetchUnread);
    const interval = setInterval(fetchUnread, 15000); // Poll every 15s for better responsiveness
    
    return () => {
      window.removeEventListener("discussionRead", fetchUnread);
      clearInterval(interval);
    };
  }, [fetchUnread, pathname]);

  useEffect(() => {
    const handleToggle = () => setIsMobileOpen(prev => !prev);
    const handleClose = () => setIsMobileOpen(false);
    window.addEventListener("toggleMobileSidebar", handleToggle);
    window.addEventListener("closeMobileSidebar", handleClose);
    
    return () => {
      window.removeEventListener("toggleMobileSidebar", handleToggle);
      window.removeEventListener("closeMobileSidebar", handleClose);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const checkStandalone = 
        window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as any).standalone === true;
      
      setIsStandalone(checkStandalone);
    }
  }, []);

  // Automatically close mobile sidebar on path change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [pathname]);

  const handleLogout = () => {
    logout();
  };

  interface NavItem {
    name: string;
    href: string;
    icon: string;
    id?: string;
  }

  // Base navigation
  const navItems: NavItem[] = [
    { name: "Dashboard", href: "/dashboard", icon: "dashboard" }
  ];

  // Role-based navigation
  switch (role) {
    case "ADMIN":
      navItems.push(
        { name: "User Management", href: "/admin/users", icon: "manage_accounts" },
        { name: "Student Management", href: "/admin/students", icon: "groups" },
        { name: "Staff Assessment", href: "/admin/staff-assessment", icon: "assignment_ind" },
        { name: "Business Status", href: "/admin/badges", icon: "military_tech" },
        { name: "AI Intelligence", href: "/admin/ai-usage", icon: "memory" },
        { name: "VIP Waitlist", href: "/admin/vip-waitlist", icon: "crown" },
        { name: "Subscriptions", href: "/admin/subscriptions", icon: "card_membership" },
        { name: "Curriculum", href: "/coach/courses", icon: "menu_book" },
        { name: "Task Approvals", href: "/taskapprovals", icon: "fact_check" },
        { name: "Task Discussions", href: "/discussions", icon: "forum" }
      );
      break;
    case "COACH":
    case "COACHING_ASSOCIATE":
      navItems.push(
        { name: "Task Approvals", href: "/taskapprovals", icon: "fact_check" },
        { name: "Tasks List", href: "/coach/tasks", icon: "list_alt" },
        { name: "Task Discussions", href: "/discussions", icon: "forum" },
        { name: "Student Assessment", href: "/coach/student-assessment", icon: "query_stats" },
        { name: "Service Desk", href: "/coach/desk", icon: "support_agent" },
        { name: "Curriculum", href: "/coach/courses", icon: "menu_book" }
      );
      break;
    case "MARKETING_ASSOCIATE":
      navItems.push(
        { name: "Task Discussions", href: "/discussions", icon: "forum" },
        { name: "Conversions", href: "/marketing/conversions", icon: "monitoring" },
        { name: "Expert Pool", href: "/marketing/pool", icon: "engineering" }
      );
      break;
    case "STUDENT":
    default:
      navItems.push(
        { name: "Courses", href: `/courses${userHighestTier > 0 ? `?tier=${userHighestTier}` : ''}`, icon: "school", id: "tour-courses" },
        { name: "Tasks", href: "/tasks", icon: "task_alt", id: "tour-tasks" }
      );

      break;
  }

  // Common footer link
  if (!isStandalone) {
    navItems.push({ name: "Install App", href: "#install", icon: "install_mobile" });
  }
  navItems.push({ name: "Settings", href: "/settings", icon: "settings" });

  const supportWhatsapp = (session?.user as any)?.support_whatsapp;
  const rawNumber = supportWhatsapp ? supportWhatsapp.replace(/\+/g, '') : "919074509112";
  const supportText = encodeURIComponent(`Hi Support Team!\nI need some help with my account.\n\nName: ${userName}\nMembership: ${membership}\n\n`);
  const supportHref = `https://wa.me/${rawNumber}?text=${supportText}`;

  return (
    <>
      <style>{`
        @media (max-width: 767px) {
          #app-sidebar {
            display: ${isMobileOpen ? 'flex' : 'none'} !important;
          }
        }
        @media (min-width: 768px) {
          #app-sidebar {
            display: flex !important;
          }
        }
      `}</style>

      {/* Backdrop overlay for mobile */}
      {isMobileOpen && (
        <div 
          onClick={() => setIsMobileOpen(false)}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 md:hidden animate-in fade-in duration-300"
        />
      )}

      <aside
        id="app-sidebar"
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
        className={`fixed left-0 top-0 h-screen bg-surface-container-low flex-col pt-20 pb-8 z-40 border-r border-outline-variant/10 transition-all duration-300 ease-in-out shadow-2xl overflow-hidden
          ${isExpanded || isMobileOpen ? "w-64 px-4" : "w-20 px-2"}
        `}
      >
        <div className={`mb-10 px-4 transition-all duration-300 ${isExpanded || isMobileOpen ? "opacity-100 max-h-28" : "opacity-0 max-h-0 overflow-hidden pointer-events-none"}`}>
          <h1 className="text-lg font-bold text-on-surface truncate" title={userName}>
            {userName}
          </h1>
          <div className="flex flex-col gap-0.5 mt-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 flex items-center gap-2">
              <span>{role.replace("_", " ")}</span>
              {role === "STUDENT" && (
                <>
                  <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                  <span className="text-primary">{membership}</span>
                </>
              )}
            </p>
          </div>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto overflow-x-hidden pb-4 pr-1">
          {navItems.map((item) => {
            const isActive = pathname === item.href.split('?')[0];
            const isInstallItem = item.href === "#install";

            if (isInstallItem) {
              return (
                <button
                  key={item.name}
                  id={(item as any).id}
                  onClick={(e) => {
                    e.preventDefault();
                    
                    const globalPrompt = (window as any).deferredPrompt;
                    if (globalPrompt) {
                      try {
                        const promptResult = globalPrompt.prompt();
                        if (promptResult && typeof promptResult.catch === "function") {
                          promptResult.catch((err: any) => {
                            console.warn("PWA prompt rejection caught in sidebar, falling back to banner:", err);
                            window.dispatchEvent(new CustomEvent("triggerPWAInstall"));
                          });
                        }
                        globalPrompt.userChoice.then((choiceResult: { outcome: string }) => {
                          if (choiceResult.outcome === "accepted") {
                            window.dispatchEvent(new CustomEvent("pwaInstalledSuccess"));
                          }
                        });
                      } catch (err) {
                        console.warn("Direct PWA prompt error from sidebar, falling back to banner:", err);
                        window.dispatchEvent(new CustomEvent("triggerPWAInstall"));
                      }
                    } else {
                      window.dispatchEvent(new CustomEvent("triggerPWAInstall"));
                    }

                    setIsMobileOpen(false);
                  }}
                  className={`w-full flex items-center rounded-xl transition-all group text-sm font-medium relative text-on-surface-variant hover:bg-surface-container-high text-left py-3 ${isExpanded || isMobileOpen ? "gap-3 px-4" : "gap-0 px-3"}`}
                >
                  <div className="min-w-[24px] flex justify-center relative">
                    <span className="material-symbols-outlined" data-icon={item.icon}>
                      {item.icon}
                    </span>
                  </div>
                  <span className={`font-inter whitespace-nowrap transition-all duration-300 ${isExpanded || isMobileOpen ? "opacity-100 translate-x-0 w-auto visible" : "opacity-0 -translate-x-4 w-0 overflow-hidden invisible pointer-events-none"}`}>
                    {item.name}
                  </span>

                  {/* Tooltip for collapsed mode */}
                  {!isExpanded && !isMobileOpen && (
                    <div className="absolute left-full ml-4 px-3 py-1 bg-on-surface text-surface text-[10px] font-bold rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                      {item.name}
                    </div>
                  )}
                </button>
              );
            }

            return (
              <Link
                key={item.name}
                to={item.href}
                id={(item as any).id}
                onClick={() => setIsMobileOpen(false)}
                className={`flex items-center rounded-xl transition-all group text-sm font-medium relative py-3 ${isExpanded || isMobileOpen ? "gap-3 px-4" : "gap-0 px-3"} ${isActive
                    ? "bg-primary text-white font-bold shadow-lg shadow-primary/20"
                    : "text-on-surface-variant hover:bg-surface-container-high transition-colors"
                  }`}
              >
                <div className="min-w-[24px] flex justify-center relative">
                  <span className={`material-symbols-outlined ${isActive ? 'fill-1' : ''}`} data-icon={item.icon}>
                    {item.icon}
                  </span>
                  {item.name === "Task Discussions" && unreadCount > 0 && (
                    <div className={`absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-surface-container-low transition-all ${isActive ? "border-primary" : ""}`} />
                  )}
                </div>
                <span className={`font-inter whitespace-nowrap transition-all duration-300 ${isExpanded || isMobileOpen ? "opacity-100 translate-x-0 w-auto visible" : "opacity-0 -translate-x-4 w-0 overflow-hidden invisible pointer-events-none"}`}>
                  {item.name}
                </span>

                {/* Tooltip for collapsed mode */}
                {!isExpanded && !isMobileOpen && (
                  <div className="absolute left-full ml-4 px-3 py-1 bg-on-surface text-surface text-[10px] font-bold rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                    {item.name}
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="mt-auto pt-4 space-y-4 border-t border-outline-variant/10">
          <button
            onClick={() => {
              setIsMobileOpen(false);
              handleLogout();
            }}
            className={`w-full flex items-center rounded-xl text-error hover:bg-error/10 cursor-pointer active:scale-95 hover:shadow-sm transition-all group text-sm font-medium py-3 ${isExpanded || isMobileOpen ? "gap-3 px-4" : "gap-0 px-3"}`}
          >
            <div className="min-w-[24px] flex justify-center">
              <span className="material-symbols-outlined group-hover:fill-1" data-icon="logout">
                logout
              </span>
            </div>
            <span className={`font-inter transition-all duration-300 ${isExpanded || isMobileOpen ? "opacity-100 translate-x-0 w-auto visible" : "opacity-0 -translate-x-4 w-0 overflow-hidden invisible pointer-events-none"}`}>
              Logout
            </span>
          </button>

          <div className={`px-4 py-2 w-full flex justify-between items-center text-on-surface-variant transition-all duration-300 ${isExpanded || isMobileOpen ? "opacity-100 max-h-10 visible" : "opacity-0 max-h-0 overflow-hidden invisible pointer-events-none"}`}>
            <span className="text-[11px] font-semibold uppercase tracking-widest">v2.4.0</span>
            <div className="flex gap-4">
              <a 
                href={supportHref} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest hover:text-green-500 cursor-pointer transition-colors"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.888-.788-1.487-1.761-1.66-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                Support
              </a>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
